import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Panel } from 'primereact/panel'

import { isAdminDomain, isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import { isConfigReadonly } from '../../../lib/is-config-readonly'
import isAura from '../../../lib/isAura'
import isOnline from '../../../lib/isOnline'
import { OcppConnectorLoadbalancerSettings } from '../../../support/components/ocpp-connector-loadbalancer-settings'
import OcppConnectorSettings from '../../../ui/OcppConnectorSettings'
import Text from '../../../ui/Text'
import {
    CHARGEPOINT_MIN_CURRENT,
    installationPhasesOptionsTN,
    installationPhasesOptionsIT,
    installationPhasesOrderOptions,
    PhaseMode,
    Phases,
} from '../../domain/electrical-settings'
import { useChargePointContext } from '../../context/ChargePointContext'
import { ChargePoint, Connector } from '../../redux/api/web-api'
import { EarthingSchemeLB } from '../../enums/earthingSchemes'
import { earthingSystemReadOnly } from '../../services/utilsService'
import { getChargerPhaseList } from '../../utils/chargePointUtils'
import { isAura2 } from '../../utils/chargePointProductUtils'
import useText from '../../hooks/useText'
import isLuna from '../../../lib/isLuna'
import { Input } from '../../uikit/Input'
import { FieldInfoButton } from '../../uikit/field-info-button'
import { Dropdown } from '../../uikit/Dropdown'
import { isChargingConnector } from '../../../lib/findChargingConnector'
import { IsSchukoConnector } from '../../../lib/findSchukoConnector'
import { SelectButton } from '../../uikit/SelectButton'

export type OcppSettingsProps = {
    chargePoint: ChargePoint
}

const OcppSettings = ({ chargePoint }: OcppSettingsProps): React.ReactElement => {
    const text = useText()
    const isAura2ChargePoint = isAura2(chargePoint)

    const configKeys = chargePoint?.ocppConfigKeys

    const { autoRefresh } = useChargePointContext()
    const {
        register,
        trigger,
        watch,
        setValue,
        control,
        formState: { errors },
    } = useFormContext()

    useEffect(() => {
        const subscription = watch(() => trigger())
        return () => subscription.unsubscribe()
    }, [trigger, watch])

    const isOffline = !isOnline(chargePoint)

    const earthingScheme = watch('earthingSystem')
    const installationPhaseMode = watch('installationPhaseMode_1')

    const phasesList = getChargerPhaseList(chargePoint?.productId, earthingScheme, installationPhaseMode)
    const phasesOptions = phasesList.map((phase) => ({ value: phase, label: phase }))

    const installationPhasesOptions =
        earthingScheme === EarthingSchemeLB.IT ? installationPhasesOptionsIT : installationPhasesOptionsTN

    const disabledPhaseSettingBasedITLuna = earthingScheme === EarthingSchemeLB.IT && isLuna(chargePoint)
    const isInstallationPhasesIsDisabledWithReason = () => {
        if (isConfigReadonly('InstallationPhases', configKeys)) return text('msgInstallationPhasesReadOnly')

        if (!isSupportDomain && chargePoint?.ocppEnabled) return text('msgInstallationPhasesDisabledByOcppProxy')
        if (disabledPhaseSettingBasedITLuna) return text('msgDisabledPhaseModesLuna')

        return undefined
    }

    const isEarthingSystemDisabledWithReason = () => {
        if (isAura2ChargePoint) return 'msgChargerTNSupportInfo'

        if (chargePoint?.loadBalancerGroupId) return 'msgChargerConnectedtoLBGroup'

        if (earthingSystemReadOnly(configKeys)) return 'msgChargerTNSupportInfo'

        return undefined
    }

    const handleEarthingSchemeChange = (earthingSchemeOption: EarthingSchemeLB) => {
        setValue('earthingSystem', earthingSchemeOption)

        if (chargePoint?.loadBalancerGroupId) {
            return
        }

        chargePoint?.connectors?.forEach((connector) => {
            const connectorId = connector.connectorId as 1 | 2
            const isLunaCharger = isLuna(chargePoint)

            setValue(`lbOfflinePhase_${connectorId}`, '')

            if (earthingSchemeOption === EarthingSchemeLB.TN) {
                const isSchuko = IsSchukoConnector(connector)
                const maxPhasesOne = installationPhaseMode !== '3P'
                setValue(
                    `installationPhase_${connectorId}`,
                    isLunaCharger || isSchuko || maxPhasesOne ? Phases.L1 : Phases.L123
                )
                setValue('installationPhases', '3')
                setValue(`installationPhaseMode_${connectorId}`, PhaseMode.threePhase)
            }
            if (earthingSchemeOption === EarthingSchemeLB.IT) {
                setValue(`installationPhase_${connectorId}`, Phases.L1L2)

                // Luna supports only 2 phases
                setValue('installationPhases', isLunaCharger ? '2' : '3')

                setValue(
                    `installationPhaseMode_${connectorId}`,
                    isLunaCharger ? PhaseMode.twoPhase : PhaseMode.threePhase
                )
            }
        })
    }

    return (
        <div className="grid">
            <div className="lg:col-6 col-12">
                <Panel
                    toggleable
                    header={
                        <span className="flex align-items-center gap-2">
                            <i className="pi pi-wrench"></i>
                            <span> {text('lblPhysicalInstallationSettings')}</span>
                        </span>
                    }
                    data-testid="physical-installation-settings-widget"
                >
                    <div className="flex align-items-center justify-content-between" data-testid="earthingSystem">
                        <Text code="lblEarthingSystem" />

                        <FieldInfoButton
                            ariaLabel={text('lblMoreInformation')}
                            text={text('msgEarthingSystem')}
                            testId="earthing-system-info-button"
                        />
                    </div>

                    <Controller
                        name="earthingSystem"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                            const disabledReasonForEarthing = isEarthingSystemDisabledWithReason()
                            return (
                                <SelectButton<EarthingSchemeLB>
                                    fullWidth
                                    id="earthingSystem"
                                    ariaLabel={text('lblEarthingSystem')}
                                    disabledReason={
                                        disabledReasonForEarthing ? text(disabledReasonForEarthing) : undefined
                                    }
                                    disabled={
                                        isAura2ChargePoint ||
                                        autoRefresh ||
                                        isOffline ||
                                        Boolean(chargePoint?.loadBalancerGroupId) ||
                                        isAdminDomain ||
                                        earthingSystemReadOnly(configKeys)
                                    }
                                    value={value}
                                    options={
                                        isAura2ChargePoint
                                            ? [
                                                  {
                                                      label: text('lblTN'),
                                                      value: EarthingSchemeLB.TN,
                                                  },
                                              ]
                                            : [
                                                  {
                                                      label: text('lblTN'),
                                                      value: EarthingSchemeLB.TN,
                                                  },
                                                  {
                                                      label: text('lblITNet'),
                                                      value: EarthingSchemeLB.IT,
                                                  },
                                              ]
                                    }
                                    onChange={(option) => {
                                        handleEarthingSchemeChange(option)
                                        onChange(option)
                                    }}
                                />
                            )
                        }}
                    />

                    <div className="mt-5 mb-2" data-testid="supplyCurrent">
                        <Input
                            type="number"
                            min={CHARGEPOINT_MIN_CURRENT}
                            label={text('lblSupplyCurrentField')}
                            infoText={text('msgSupplyCurrentField')}
                            id="supplyCurrent"
                            disabled={autoRefresh || (!isSupportDomain && chargePoint?.ocppEnabled) || isOffline}
                            data-testid={'supplyCurrent-input'}
                            error={
                                (isPartnerDomain && !chargePoint?.ocppEnabled) || isSupportDomain || isAdminDomain
                                    ? errors.supplyCurrent?.message
                                    : null
                            }
                            {...register('supplyCurrent')}
                        />
                    </div>
                    <div className="mt-5 mb-2" data-testid="installationPhasesDiv">
                        <Controller
                            name="installationPhases"
                            control={control}
                            render={({ field: { value, onChange } }) => {
                                const disabledReason = isInstallationPhasesIsDisabledWithReason()
                                return (
                                    <Dropdown
                                        label={text('lblInstallationPhases')}
                                        infoText={text('msgInstallationPhase')}
                                        id="installationPhases"
                                        options={installationPhasesOptions}
                                        disabled={autoRefresh || isOffline || !!disabledReason}
                                        disabledReason={disabledReason}
                                        data-testid="installationPhases-select"
                                        value={value}
                                        onChange={(option) => {
                                            onChange(option.value)
                                            chargePoint?.connectors?.map((nextConnector: Connector) => {
                                                setValue(
                                                    `installationPhaseMode_${nextConnector.connectorId}`,
                                                    `${option.value}P`
                                                )
                                            })
                                        }}
                                    />
                                )
                            }}
                        />
                    </div>
                    <div className="mt-5 mb-2" data-testid="offlineCurrent">
                        <Controller
                            name="connectorPhaseRotation"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    id="connectorPhaseRotation"
                                    label={text('lblInstallationPhaseOrder')}
                                    infoText={text('msgInstallationPhaseOrder')}
                                    options={installationPhasesOrderOptions}
                                    disabled={
                                        autoRefresh ||
                                        isOffline ||
                                        isConfigReadonly('ConnectorPhaseRotation', configKeys) ||
                                        (!isSupportDomain && chargePoint?.ocppEnabled)
                                    }
                                    data-testid="connectorPhaseRotation-value"
                                    value={value}
                                    onChange={(option) => {
                                        onChange(option.value)
                                    }}
                                />
                            )}
                        />
                    </div>
                    {isSupportDomain && isAura(chargePoint) && (
                        <span className="flex">
                            <input
                                type="checkbox"
                                className="checkbox-black mt-5 ml-2"
                                id="allowConnectCurrent"
                                disabled={autoRefresh}
                                data-testid="allow-connect-input"
                                {...register('allowConnectCurrent')}
                            />

                            <span className="mt-5 ml-2">
                                <Text code="lblAllowConnectCurrentField" />
                            </span>
                        </span>
                    )}
                </Panel>
                {chargePoint?.loadBalancerGroupId && (
                    <Panel
                        className="mt-3"
                        data-testid="loadbalander-settings-widget"
                        toggleable
                        header={
                            <>
                                <span className="fa fa-gears mr-2 text-500"></span>
                                <Text code="lblLoadbalancerSettings" />
                            </>
                        }
                    >
                        {chargePoint.loadBalancerGroupId &&
                            chargePoint.connectors?.filter(isChargingConnector)?.map((connector) => (
                                <div key={connector.connectorId}>
                                    <OcppConnectorLoadbalancerSettings
                                        isOnline={isOnline(chargePoint)}
                                        chargePoint={chargePoint}
                                        connector={connector}
                                        isLocked={autoRefresh}
                                        phasesOptions={phasesOptions}
                                    />
                                </div>
                            ))}
                    </Panel>
                )}
            </div>
            <Panel
                data-testid="connector-configuration-widget"
                className="lg:col-6 col-12"
                toggleable
                header={
                    <span className="mt-2 flex">
                        <i className="pi pi-cog"></i>
                        <span className="ml-2"> {text('lblConnectorConfiguration')}</span>
                    </span>
                }
            >
                {chargePoint &&
                    chargePoint.connectors?.filter(isChargingConnector)?.map((connector) => (
                        <div key={connector.connectorId}>
                            <OcppConnectorSettings
                                isLocked={autoRefresh}
                                isOnline={isOnline(chargePoint)}
                                chargePoint={chargePoint}
                                connector={connector}
                                phasesOptions={phasesOptions}
                            />
                        </div>
                    ))}
            </Panel>
        </div>
    )
}

export default OcppSettings
